import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  FiPlus, 
  FiSearch, 
  FiTrash2,
  FiKey,
  FiCheck,
  FiX,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { codigosService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const Licencias = () => {
  const [filtros, setFiltros] = useState({
    utilizado: '',
    codigo: ''
  });
  
  const [paginacion, setPaginacion] = useState({
    pagina: 1,
    limite: 25 // Aumentado de 10 a 25 para reducir número de peticiones
  });
  const [modalCrear, setModalCrear] = useState(false);
  const [modalVerificar, setModalVerificar] = useState(false);
  const [formData, setFormData] = useState({
    cantidad: 1,
    notas: ''
  });
  const [codigoVerificar, setCodigoVerificar] = useState('');
  const [resultadoVerificacion, setResultadoVerificacion] = useState(null);

  const queryClient = useQueryClient();

  // Obtener códigos
  const { data: codigosData, isLoading, error } = useQuery(
    ['codigos', filtros, paginacion],
    async () => {
      const params = {
        ...paginacion,
        ...filtros
      };
      
      // Solo incluir filtros que tengan valor
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });
      
      const response = await codigosService.obtenerTodos(params);
      return response.data;
    }
  );

  // Obtener estadísticas
  const { data: estadisticas } = useQuery(
    'estadisticas-codigos',
    async () => {
      const response = await codigosService.estadisticas();
      return response.data;
    }
  );

  // Mutación para crear códigos
  const crearCodigos = useMutation(
    async (datos) => {
      const response = await codigosService.crear(datos);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.mensaje);
        setModalCrear(false);
        setFormData({ cantidad: 1, notas: '' });
        queryClient.invalidateQueries(['codigos']);
        queryClient.invalidateQueries('estadisticas-codigos');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al crear códigos');
      }
    }
  );

  // Mutación para verificar código
  const verificarCodigo = useMutation(
    async (codigo) => {
      const response = await codigosService.verificar(codigo);
      return response.data;
    },
    {
      onSuccess: (data) => {
        setResultadoVerificacion(data);
        if (data.valido) {
          toast.success(data.mensaje);
        } else {
          toast.error(data.mensaje);
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al verificar código');
      }
    }
  );

  // Mutación para marcar código como utilizado
  const marcarUtilizado = useMutation(
    async (id) => {
      const response = await codigosService.marcarUtilizado(id);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.mensaje);
        queryClient.invalidateQueries(['codigos']);
        queryClient.invalidateQueries('estadisticas-codigos');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al marcar código como utilizado');
      }
    }
  );

  // Mutación para marcar código como no utilizado
  const marcarNoUtilizado = useMutation(
    async (id) => {
      const response = await codigosService.marcarNoUtilizado(id);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.mensaje);
        queryClient.invalidateQueries(['codigos']);
        queryClient.invalidateQueries('estadisticas-codigos');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al marcar código como no utilizado');
      }
    }
  );

  // Mutación para eliminar código
  const eliminarCodigo = useMutation(
    async (id) => {
      const response = await codigosService.eliminar(id);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.mensaje);
        queryClient.invalidateQueries(['codigos']);
        queryClient.invalidateQueries('estadisticas-codigos');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al eliminar código');
      }
    }
  );

  const handleSubmitCrear = (e) => {
    e.preventDefault();
    crearCodigos.mutate({ cantidad: formData.cantidad });
  };

  const handleSubmitVerificar = (e) => {
    e.preventDefault();
    if (codigoVerificar.trim()) {
      verificarCodigo.mutate(codigoVerificar.trim());
    }
  };

  const handleCambioFiltro = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
    setPaginacion(prev => ({ ...prev, pagina: 1 }));
  };

  const handleCambiarPagina = (nuevaPagina) => {
    setPaginacion(prev => ({ ...prev, pagina: nuevaPagina }));
  };

  const handleCambiarLimite = (nuevoLimite) => {
    setPaginacion({ pagina: 1, limite: nuevoLimite });
  };

  const obtenerEstadoClases = (utilizado) => {
    return utilizado === 'S' 
      ? 'status-badge status-used' 
      : 'status-badge status-available';
  };

  const obtenerEstadoTexto = (utilizado) => {
    return utilizado === 'S' ? 'Utilizado' : 'Disponible';
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error al cargar los códigos</p>
        <pre className="mt-4 text-xs bg-gray-100 p-4 rounded overflow-auto">
          {JSON.stringify(error, null, 2)}
        </pre>
      </div>
    );
  }

  const codigos = codigosData?.data || [];
  const paginacionData = codigosData?.paginacion || {};

  return (
    <>
      <style>{`
        .status-badge {
          @apply inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium;
        }
        .status-available {
          @apply bg-green-100 text-green-800;
        }
        .status-used {
          @apply bg-red-100 text-red-800;
        }
      `}</style>
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Códigos de Licencia</h1>
            <p className="text-gray-600">Gestiona los códigos de activación del sistema</p>
          </div>
          <div className="flex space-x-3 mt-4 sm:mt-0">
            <button 
              onClick={() => setModalVerificar(true)}
              className="btn-secondary flex items-center"
            >
              <FiKey size={16} className="mr-2" />
              Verificar Código
            </button>
            <button 
              onClick={() => setModalCrear(true)}
              className="btn-primary flex items-center"
            >
              <FiPlus size={16} className="mr-2" />
              Generar Códigos
            </button>
          </div>
        </div>

        {/* Estadísticas */}
        {estadisticas && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FiKey className="text-blue-600" size={20} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Códigos</p>
                  <p className="text-2xl font-semibold text-gray-900">{estadisticas.total || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="card">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FiCheck className="text-green-600" size={20} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Disponibles</p>
                  <p className="text-2xl font-semibold text-gray-900">{estadisticas.disponibles || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="card">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <FiX className="text-red-600" size={20} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Utilizados</p>
                  <p className="text-2xl font-semibold text-gray-900">{estadisticas.utilizados || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="card">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <FiRefreshCw className="text-yellow-600" size={20} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Creados Hoy</p>
                  <p className="text-2xl font-semibold text-gray-900">{estadisticas.creados_hoy || 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="card">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar Código
              </label>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Buscar códigos..."
                  className="input-field pl-10"
                  value={filtros.codigo}
                  onChange={(e) => handleCambioFiltro('codigo', e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                className="input-field"
                value={filtros.utilizado}
                onChange={(e) => handleCambioFiltro('utilizado', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="N">Disponibles</option>
                <option value="S">Utilizados</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mostrar
              </label>
              <select
                className="input-field"
                value={paginacion.limite}
                onChange={(e) => handleCambiarLimite(parseInt(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>

        {/* Lista de códigos */}
        <div className="card">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Código
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha Creación
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {codigos.map((codigo) => (
                  <tr key={codigo.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono font-medium text-gray-900">
                        {codigo.codigo}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={obtenerEstadoClases(codigo.utilizado)}>
                        {obtenerEstadoTexto(codigo.utilizado)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(codigo.fecha_creacion).toLocaleString('es-ES')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex flex-wrap gap-1 justify-center">
                        {codigo.utilizado === 'N' ? (
                          <button
                            onClick={() => marcarUtilizado.mutate(codigo.id)}
                            disabled={marcarUtilizado.isLoading}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Marcar como utilizado"
                          >
                            <FiCheck size={12} className="mr-1" />
                            <span className="hidden sm:inline">Usar</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => marcarNoUtilizado.mutate(codigo.id)}
                            disabled={marcarNoUtilizado.isLoading}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-yellow-100 text-yellow-700 hover:bg-yellow-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Liberar código"
                          >
                            <FiRefreshCw size={12} className="mr-1" />
                            <span className="hidden sm:inline">Liberar</span>
                          </button>
                        )}
                        
                        <button
                          onClick={() => eliminarCodigo.mutate(codigo.id)}
                          disabled={eliminarCodigo.isLoading}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Eliminar código"
                        >
                          <FiTrash2 size={12} className="mr-1" />
                          <span className="hidden sm:inline">Eliminar</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {paginacionData && paginacionData.paginas > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handleCambiarPagina(paginacion.pagina - 1)}
                  disabled={paginacion.pagina === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={() => handleCambiarPagina(paginacion.pagina + 1)}
                  disabled={paginacion.pagina === paginacionData.paginas}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Mostrando{' '}
                    <span className="font-medium">{((paginacion.pagina - 1) * paginacion.limite) + 1}</span>
                    {' '}a{' '}
                    <span className="font-medium">
                      {Math.min(paginacion.pagina * paginacion.limite, paginacionData.total)}
                    </span>
                    {' '}de{' '}
                    <span className="font-medium">{paginacionData.total}</span>
                    {' '}resultados
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handleCambiarPagina(paginacion.pagina - 1)}
                      disabled={paginacion.pagina === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Anterior</span>
                      <FiChevronLeft className="h-5 w-5" />
                    </button>
                    
                    {/* Números de página */}
                    {Array.from({ length: Math.min(5, paginacionData.paginas) }, (_, i) => {
                      let pagina;
                      if (paginacionData.paginas <= 5) {
                        pagina = i + 1;
                      } else if (paginacion.pagina <= 3) {
                        pagina = i + 1;
                      } else if (paginacion.pagina >= paginacionData.paginas - 2) {
                        pagina = paginacionData.paginas - 4 + i;
                      } else {
                        pagina = paginacion.pagina - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pagina}
                          onClick={() => handleCambiarPagina(pagina)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pagina === paginacion.pagina
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pagina}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handleCambiarPagina(paginacion.pagina + 1)}
                      disabled={paginacion.pagina === paginacionData.paginas}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Siguiente</span>
                      <FiChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Crear Códigos */}
      {modalCrear && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Generar Códigos</h3>
              <form onSubmit={handleSubmitCrear}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cantidad
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    className="input-field w-full"
                    value={formData.cantidad}
                    onChange={(e) => setFormData(prev => ({ ...prev, cantidad: parseInt(e.target.value) }))}
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notas (opcional)
                  </label>
                  <textarea
                    className="input-field w-full"
                    rows="3"
                    value={formData.notas}
                    onChange={(e) => setFormData(prev => ({ ...prev, notas: e.target.value }))}
                    placeholder="Notas sobre estos códigos..."
                  />
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setModalCrear(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={crearCodigos.isLoading}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {crearCodigos.isLoading ? 'Generando...' : 'Generar Códigos'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Verificar Código */}
      {modalVerificar && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Verificar Código</h3>
              <form onSubmit={handleSubmitVerificar}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Código
                  </label>
                  <input
                    type="text"
                    className="input-field w-full font-mono"
                    value={codigoVerificar}
                    onChange={(e) => setCodigoVerificar(e.target.value.toUpperCase())}
                    placeholder="ABC-123"
                    required
                  />
                </div>
                
                {resultadoVerificacion && (
                  <div className={`mb-4 p-3 rounded-md ${
                    resultadoVerificacion.valido 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    <p className="text-sm font-medium">
                      {resultadoVerificacion.mensaje}
                    </p>
                  </div>
                )}
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setModalVerificar(false);
                      setCodigoVerificar('');
                      setResultadoVerificacion(null);
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cerrar
                  </button>
                  <button
                    type="submit"
                    disabled={verificarCodigo.isLoading}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {verificarCodigo.isLoading ? 'Verificando...' : 'Verificar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Licencias; 